import React, { useRef, forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePreserveSearch } from '../../../../Hooks/usePreserveSearch';
import { ChatLanguage, ChatStrings } from '../../i18n';
import { AlertIcon, ArrowUpIcon, CloseIcon, InfoIcon, MicIcon, WaitRing } from '../Icons';
import { canRecordVoice } from '../../hooks/useVoiceRecorder';
import { useVoiceQuestion, VoiceNotice } from '../../hooks/useVoiceQuestion';
import { VoiceMeter } from './VoiceMeter';

interface ChatInputProps {
   input: string;
   setInput: (value: string) => void;
   onSend: () => void;
   /** Sends words the guest said as their message; without it there is no microphone. */
   onSendText?: (text: string) => void;
   isConnected: boolean;
   isConnecting: boolean;
   canTypeMessages: boolean;
   strings: ChatStrings;
   language: ChatLanguage;
}

export interface ChatInputRef {
   focus: () => void;
}

const MIN_HEIGHT = 44;
const MAX_HEIGHT = 200;

/** Notices that something went wrong (persimmon); the others only say what to do next. */
const PROBLEMS: VoiceNotice[] = ['blocked', 'failed'];

const noticeText = (notice: VoiceNotice, strings: ChatStrings) =>
   ({
      nothing: strings.voiceNothing,
      blocked: strings.voiceBlocked,
      noMic: strings.voiceNoMic,
      failed: strings.voiceFailed,
      tooMany: strings.voiceTooMany,
      unavailable: strings.voiceUnavailable
   })[notice];

/** The time spoken, as m:ss. */
const clock = (ms: number) => {
   const seconds = Math.floor(ms / 1000);
   return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

/**
 * The composer: a field that grows with the question and the one navy element on the screen, the send circle.
 * It never moves; connection trouble is said in a line above it, not by taking the composer away.
 * With the field empty the circle is the microphone: the guest says the question instead, the field gives way to the
 * voice meter while they speak, and the words are sent as their message once they stop (or tap send).
 */
const ChatInput = forwardRef<ChatInputRef, ChatInputProps>(
   ({ input, setInput, onSend, onSendText, isConnected, isConnecting, canTypeMessages, strings, language }, ref) => {
      const textareaRef = useRef<HTMLTextAreaElement | null>(null);
      const discRef = useRef<HTMLButtonElement | null>(null);
      const { privacyPath } = usePreserveSearch();
      const voiceSupported = useMemo(() => !!onSendText && canRecordVoice(), [onSendText]);
      const voice = useVoiceQuestion({ onHeard: (text) => onSendText?.(text), language });
      const speaking = voice.phase !== 'idle';

      const adjustHeight = () => {
         const textarea = textareaRef.current;
         if (!textarea) return;
         textarea.style.height = 'auto';
         const next = Math.min(textarea.scrollHeight, MAX_HEIGHT);
         textarea.style.height = `${Math.max(next, MIN_HEIGHT)}px`;
         textarea.style.overflowY = textarea.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
      };

      useEffect(() => {
         adjustHeight();
      }, [input]);

      // Typing is the guest's answer to whatever the microphone said
      const { dismiss } = voice;
      useEffect(() => {
         if (input) dismiss();
      }, [input, dismiss]);

      const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
         if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            onSend();
         }
      };

      useImperativeHandle(ref, () => ({
         focus: () => textareaRef.current?.focus()
      }));

      const disabled = !canTypeMessages || !isConnected;
      const showReconnecting = canTypeMessages && !isConnected && isConnecting;
      const showOffline = canTypeMessages && !isConnected && !isConnecting;
      const showMic = voiceSupported && !speaking && !input.trim();

      const cancelSpeaking = () => {
         voice.cancel();
         // Cancel leaves with the meter: the circle (the microphone again) keeps the focus
         discRef.current?.focus();
      };

      const handleRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
         if (e.key === 'Escape' && speaking) {
            e.preventDefault();
            cancelSpeaking();
         }
      };

      // One circle for the three jobs, so the focus stays on it from the microphone to sending what was said. While it
      // waits (for the microphone, for the words) it is only marked as unavailable: a disabled button would drop the focus.
      const disc = speaking
         ? {
              label: strings.send,
              onClick: () => voice.phase === 'listening' && void voice.finish(),
              disabled: false,
              waiting: voice.phase !== 'listening',
              icon: voice.phase === 'writing' ? <WaitRing className="cx-ring" /> : <ArrowUpIcon />
           }
         : showMic
           ? { label: strings.speak, onClick: () => void voice.start(), disabled, waiting: false, icon: <MicIcon /> }
           : // Sending is allowed while an earlier turn is still being answered: every send is its own turn.
             { label: strings.send, onClick: onSend, disabled: disabled || !input.trim(), waiting: false, icon: <ArrowUpIcon /> };

      const voiceStatus = (() => {
         if (showReconnecting || showOffline) return null;
         if (voice.phase === 'starting') return voice.asking ? { text: strings.voiceAllow, icon: <InfoIcon /> } : null;
         if (voice.phase === 'listening') return { text: strings.voiceListening, icon: <MicIcon /> };
         if (voice.phase === 'writing') return { text: strings.voiceWriting, icon: <WaitRing className="cx-ring" /> };
         if (voice.notice) {
            const problem = PROBLEMS.includes(voice.notice);
            return { text: noticeText(voice.notice, strings), icon: problem ? <AlertIcon /> : <InfoIcon />, problem };
         }
         return null;
      })();

      return (
         <div className="cx-composer">
            <div className="cx-measure">
               <div className="cx-capsule cx-frosted cx-frosted--thick">
                  <p className="cx-disclosure" lang={language}>
                     {strings.aiDisclosure} <Link to={privacyPath}>{strings.privacy}</Link>
                  </p>
                  {showReconnecting && (
                     <p className="cx-composer__status" role="status" aria-live="polite">
                        <WaitRing className="cx-ring" />
                        <span>{strings.reconnecting}</span>
                     </p>
                  )}
                  {showOffline && (
                     <p className="cx-composer__status cx-composer__status--offline" role="status" aria-live="polite">
                        <AlertIcon />
                        <span>{strings.offline}</span>
                     </p>
                  )}
                  {voiceStatus && (
                     <p className={`cx-composer__status${voiceStatus.problem ? ' cx-composer__status--offline' : ''}`} role="status" aria-live="polite">
                        {voiceStatus.icon}
                        <span>{voiceStatus.text}</span>
                     </p>
                  )}
                  {/* What was heard is on screen as the guest's message; a screen reader hears it once, here */}
                  <p className="cx-sr-only" role="status" aria-live="polite">
                     {voice.sent && !speaking ? strings.voiceSent(voice.sent) : ''}
                  </p>
                  <div className="cx-composer__row" onKeyDown={handleRowKeyDown}>
                     {speaking && (
                        <button
                           key="cancel"
                           type="button"
                           className="cx-voice-cancel"
                           onClick={cancelSpeaking}
                           aria-label={strings.voiceCancel}
                           title={strings.voiceCancel}
                        >
                           <CloseIcon />
                        </button>
                     )}
                     {speaking ? (
                        <div key="meter" className={`cx-voice-meter${voice.phase === 'writing' ? ' cx-voice-meter--still' : ''}`}>
                           <VoiceMeter readLevel={voice.readLevel} live={voice.phase === 'listening'} />
                           <span className="cx-voice-meter__time" aria-hidden="true">
                              {clock(voice.elapsedMs)}
                           </span>
                        </div>
                     ) : (
                        <div key="field" className={`cx-composer__field${disabled ? ' cx-composer__field--disabled' : ''}`}>
                           <textarea
                              ref={textareaRef}
                              className="cx-composer__input"
                              value={input}
                              onChange={(e) => setInput(e.target.value)}
                              onKeyDown={handleKeyDown}
                              placeholder={strings.placeholder}
                              aria-label={strings.placeholder}
                              disabled={disabled}
                              rows={1}
                              dir="auto"
                              enterKeyHint="send"
                              autoComplete="off"
                              style={{ minHeight: `${MIN_HEIGHT}px`, maxHeight: `${MAX_HEIGHT}px` }}
                           />
                        </div>
                     )}
                     <button
                        key="disc"
                        ref={discRef}
                        type="button"
                        className="cx-send"
                        onClick={disc.onClick}
                        disabled={disc.disabled}
                        aria-disabled={disc.waiting || undefined}
                        aria-label={disc.label}
                        title={disc.label}
                     >
                        {disc.icon}
                     </button>
                  </div>
               </div>
            </div>
         </div>
      );
   }
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
